import React, {memo, useCallback, useRef, useEffect} from 'react';
import {View} from 'react-native';
import {createUseStyle} from '@redlink/assets/styles';
import {Card, LoadingContainer, Space} from '@redlink/components/atoms';
import {FormActions, FormCommands} from '@redlink/components/muscles';
import {useTimingTransition, mix} from 'react-native-redash';
import Animated from 'react-native-reanimated';

export const Form = memo(
    ({
         actions,
         commands,
         style,
         children,
         isBusy,
         childPageIsVisible = false,
         actionsStyle,
         scrollRef,
         HeaderComponent,
     }) => {
        const {styles} = useStyle();
        const containerRef = useRef(null);
        const offsetY = useRef(new Animated.Value(0));
        const x = useRef(new Animated.Value(0));
        const y = useRef(new Animated.Value(0));
        const width = useRef(new Animated.Value(0));
        const height = useRef(new Animated.Value(0));

        const onLayout = useCallback(() => {
            containerRef.current &&
            containerRef.current.measure((fx, fy, width_, height_, px, py) => {
                x.current.setValue(px);
                y.current.setValue(py);
                width.current.setValue(width_);
                height.current.setValue(height_);
            });
        }, []);

        const child =
            typeof children === 'function'
                ? children({
                    offsetY: offsetY.current,
                    x: x.current,
                    y: y.current,
                    width: width.current,
                    height: height.current,
                })
                : children;

        const progress = useTimingTransition(childPageIsVisible);
        const actionContainerOpacity = mix(progress, 1, 0);
        const actionContainerHeight = mix(progress, 40, 0);

        return (
            <LoadingContainer {...{isBusy}}>
                <Card style={[styles.container, style]}>
                    <View
                        style={styles.contentContainer}
                        ref={containerRef}
                        {...{onLayout}}
                    >
                        {HeaderComponent}
                        <Animated.ScrollView
                            ref={scrollRef}
                            scrollEventThrottle={1}
                            onScroll={({
                                           nativeEvent: {
                                               contentOffset: {y: _y},
                                           },
                                       }) => {
                                offsetY.current.setValue(_y);
                            }}
                            scrollEnabled={!childPageIsVisible}
                            showsVerticalScrollIndicator={false}
                        >
                            {child}
                        </Animated.ScrollView>
                    </View>
                    {commands && <FormCommands {...{commands}} />}
                    <Space size={8}/>
                    {actions && (
                        <Animated.View
                            style={{
                                overflow: 'hidden',
                                opacity: actionContainerOpacity,
                                height: actionContainerHeight,
                            }}
                        >
                            <FormActions {...{actions, actionsStyle}} />
                        </Animated.View>
                    )}
                </Card>
            </LoadingContainer>
        );
    }
);

const useStyle = createUseStyle(({colors, size}) => ({
    container: {
        flex: 1,
        // eslint-disable-next-line no-undef
        ...(__MOBILE__ && {borderRadius: 0}),
    },
    titleText: {
        fontSize: 14,
        color: colors.blue1,
        textAlign: 'center',
    },
    contentContainer: {
        flex: 1,
    },
}));
